import React from "react";
import styled from "styled-components/macro";
import {
    Box,
    Grid,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    List,
    ListItem,
} from "@material-ui/core";
import {useLocation} from "react-router-dom";
import {GET_APP_LOCALIZE_TEXT} from "../../utils/dq_lc_service/lc_service";

// Replaceable table data (API-ready)
const recommendationsTableData = [
    {
        category: "Internet Connectivity",
        risks:
            "Limited or no internet access can negatively impact educational opportunities, digital inclusion, and overall development (Livingstone & Helsper, 2007).",
        recommendations: [
            "Ensure your child has consistent and safe internet access at home.",
            "Utilize community resources such as libraries or school facilities if home connectivity is limited.",
            "Support equitable digital inclusion by advocating for community and school internet access programs.",
        ],
    },
    {
        category: "Device Access",
        risks:
            "Early personal device ownership without adequate digital literacy education increases risks including exposure to inappropriate content, online predators, and cyberbullying (Hinduja & Patchin, 2019).",
        recommendations: [
            "Introduce digital devices gradually, paired with structured digital literacy education.",
            "Implement effective parental controls and regularly discuss responsible device use with your child.",
            "Clearly define and communicate guidelines around device ownership and usage.",
        ],
    },
    {
        category: "Weekly Screen Time",
        risks:
            "High entertainment-focused screen time correlates with increased cyber risk and mental and physical health issues, including anxiety, depression, and sleep disturbances (Twenge & Campbell, 2018).",
        recommendations: [
            "Set clear, consistent limits for entertainment-focused screen time.",
            "Encourage alternative offline activities such as sports, hobbies, and family interactions.",
            "Regularly discuss with your child the mental and physical impacts of excessive screen time.",
        ],
    },
    {
        category: "Digital Media Engagement",
        risks:
            "Early online social interactions without proper digital literacy education may increase risks such as cyberbullying, privacy breaches, and exposure to inappropriate content (Livingstone & Stoilova, 2021).",
        recommendations: [
            "Educate your child on responsible online behavior and digital etiquette.",
            "Regularly monitor and guide your child's digital interactions.",
            "Foster open communication, enabling your child to share their digital experiences comfortably.",
        ],
    },
    {
        category: "Social Media Engagement",
        risks:
            "Frequent and extensive social media use, especially among users under 13, increases risks such as cyberbullying, irresponsible posting practices, exposure to harmful content, and potential mental health issues (O'Keeffe & Clarke-Pearson, 2011).",
        recommendations: [
            "Adhere to social media platform age restrictions and educate your child on associated risks.",
            "Discuss privacy settings, responsible posting practices, and managing their digital footprint.",
            "Engage actively and regularly in conversations about your child's social media experiences.",
        ],
    },
    {
        category: "Social Media Engagement",
        risks:
            "Use of generative AI tools without proper digital literacy education may expose children to risks of misinformation, privacy violations, digital safety, and the importance of critically evaluating AI-based content.",
        recommendations: [
            "Provide clear information and guidance on the responsible use and potential risks of artificial intelligence tools.",
            "Discuss digital citizenship, digital safety, and the importance of critically evaluating AI-generated content.",
            "Encourage your child to ask questions and critically assess the reliability of AI-based information.",
        ],
    },
];

// Styled components for layout
const TablePaper = styled(Paper)`
    padding: 0;
    background: #fafbfc;
    max-width: 950px;
    min-width: 950px;
    box-shadow: none;
`;

const CategoryCell = styled(TableCell)`
    && {
        font-weight: 700;
        font-size: 14px;
        background: #fff;
        color: #222;
        border: 1px solid #ccc;
        min-width: 180px;
        width: 180px;
        vertical-align: middle;
        text-align: center;
    }
`;

const RisksCell = styled(TableCell)`
    && {
        font-size: 14px;
        background: #fff;
        color: #222;
        border: 1px solid #ccc;
        vertical-align: middle;
        text-align: left;
        width: 330px;
    }
`;

const RecommendationsCell = styled(TableCell)`
    && {
        font-size: 14px;
        background: #fff;
        color: #222;
        border: 1px solid #ccc;
        vertical-align: middle;
        text-align: left;
        width: 430px;
        padding-left: 16px;
        padding-right: 12px;
    }
`;

const TableHeaderCell = styled(TableCell)`
    && {
        font-size: 15px;
        font-weight: 700;
        background: #263249;
        color: #fff;
        border: 1px solid #ccc;
        text-align: center;
        padding-left: 16px;
        padding-right: 0;
        vertical-align: middle;
    }
`;

const CenteredLeft = styled(Box)`
  min-height: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

function ParentRecommendations() {
    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];
    
    return (
        <Box px={{ xs: 1, md: 4 }} py={5} width="100%">
            <Grid container spacing={1} alignItems="flex-start">
                {/* LEFT: Centered and Black */}
                <Grid item xs={12} md={4} style={{marginTop:'30px'}}>
                    <CenteredLeft>
                        <Box textAlign="left" width="100%" pr={2}>
                            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 18, color: "#111" }}>
                                {GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__title_parent") || "Recommendation for Parents"}
                            </Typography>
                            <Typography variant="body1" style={{ fontSize: 17, color: "#111", marginBottom: 12 }}>
                                {GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__description_parent") || "As parents, guiding your child's digital engagement is crucial for ensuring their safety, well-being, and readiness for a rapidly evolving digital world."}
                            </Typography>
                            <Typography variant="body1" style={{ fontSize: 17, color: "#111", marginBottom: 14 }}>
                                {GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__description_parent_2") || "These recommendations highlight potential risks associated with key areas of digital use and provide practical, research-backed strategies to support you in fostering positive, responsible, and safe digital habits in your children."}
                            </Typography>
                            <Typography style={{ marginTop: 22, fontSize: 17, color: "#111" }}>
                                {GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__thank_you") || "Thank you."}
                            </Typography>
                            <Typography variant="body2" style={{ marginTop: 22, fontSize: 15, color: "#111" }}>
                                {GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__contact_note") || "If you have any questions on this report, please contact us at:"}
                            </Typography>
                            <Typography style={{ color: "#e36c33", fontWeight: 600, fontSize: 15, marginTop: 3 }}>
                                <a href="mailto:insider@dqforall.com" style={{ color: "#e36c33", textDecoration: "none" }}>insider@dqforall.com</a>
                            </Typography>
                        </Box>
                    </CenteredLeft>
                </Grid>

                {/* RIGHT: Table */}
                <Grid item xs={12} md={8}>
                        <TableContainer>
                            <Table style={{ borderCollapse: "collapse" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell>{GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__assessment_category") || "Assessment Category"}</TableHeaderCell>
                                        <TableHeaderCell>{GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__potential_risks") || "Potential Risks"}</TableHeaderCell>
                                        <TableHeaderCell>{GET_APP_LOCALIZE_TEXT(pageLc, "recommendations__recommendations") || "Recommendations"}</TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recommendationsTableData.map((row, idx) => (
                                        <TableRow key={row.category + idx} style={{ background: "#fff" }}>
                                            <CategoryCell>{row.category}</CategoryCell>
                                            <RisksCell>{row.risks}</RisksCell>
                                            <RecommendationsCell>
                                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                                    {row.recommendations.map((rec, i) => (
                                                        <li key={i} style={{ marginBottom: 3 }}>{rec}</li>
                                                    ))}
                                                </ul>
                                            </RecommendationsCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ParentRecommendations;
